import React, { createContext, useContext, useState, useEffect } from "react";

export const AuthContext = createContext();

const API_URL = process.env.REACT_APP_API_URL;

// Common fetch configuration
const fetchConfig = {
  credentials: "include",
  headers: {
    Accept: "application/json",
    "Content-Type": "application/json",
  },
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const response = await fetch(`${API_URL}/auth/me`, {
          ...fetchConfig,
          method: "GET",
        });
        setIsAuthenticated(response.ok);
      } catch (error) {
        console.error("Auth check error:", error);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };
    checkAuth();
  }, []);

  const login = async (username, password) => {
    try {
      const response = await fetch(`${API_URL}/auth/login`, {
        ...fetchConfig,
        method: "POST",
        body: JSON.stringify({ username, password }),
      });
      // Debug: log response status and body for troubleshooting
      console.log("Login response status:", response.status);
      const respText = await response.text();
      console.log("Login response body:", respText);
      // Attempt to parse JSON if response is OK
      let data;
      try {
        data = JSON.parse(respText);
      } catch {}

      if (!response.ok) {
        console.error(
          "Login failed with status",
          response.status,
          "and body",
          respText
        );
        throw new Error("Invalid credentials");
      }

      setIsAuthenticated(true);
      return true;
    } catch (error) {
      console.error("Login error:", error);
      return false;
    }
  };

  const logout = async () => {
    try {
      await fetch(`${API_URL}/auth/logout`, {
        ...fetchConfig,
        method: "POST",
      });
    } finally {
      setIsAuthenticated(false);
    }
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
